import type { ContactTopic } from '@kiao-voice/core'
import { getWebApiBaseUrl } from '../../lib/api'

export type { ContactTopic }

export type ContactErrorCode =
  /** The API rejected the payload (VineJS) — the form should not have let this through. */
  | 'invalid'
  | 'rate_limited'
  /** The API accepted the request but the mail provider rejected the send. */
  | 'send_failed'
  | 'unknown'

export class ContactSubmitError extends Error {
  readonly code: ContactErrorCode

  constructor(code: ContactErrorCode, message: string) {
    super(message)
    this.name = 'ContactSubmitError'
    this.code = code
  }
}

export interface ContactMessageInput {
  name: string
  email: string
  topic: ContactTopic
  message: string
  /**
   * Honeypot value, sent only when the hidden field was filled. A human never
   * sees that field, so anything here tells the API a bot submitted the form —
   * it answers 202 and sends nothing. Part of the type rather than cast in at
   * the call site so the payload shape stays checked.
   */
  website?: string
}

function codeForStatus(status: number): ContactErrorCode {
  switch (status) {
    case 422:
      return 'invalid'
    case 429:
      return 'rate_limited'
    case 502:
      return 'send_failed'
    default:
      return 'unknown'
  }
}

/**
 * Hand one contact-form submission to the API, which mails it to the support
 * inbox with the sender as reply-to.
 *
 * Rejects with a {@link ContactSubmitError} carrying a narrowed code so the form
 * can show a localized message per failure — a rate limit and a mail-provider
 * outage need different copy (wait vs. retry-or-email-us-directly).
 */
export async function submitContactMessage(
  input: ContactMessageInput,
  opts: { baseUrl?: string } = {}
): Promise<void> {
  const base = opts.baseUrl ?? getWebApiBaseUrl()
  const response = await fetch(`${base}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!response.ok) {
    throw new ContactSubmitError(codeForStatus(response.status), `HTTP ${response.status}`)
  }
}
